"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { MyStreamWaveTab } from "@/types/waves.types";
import useLocalPreference from "@/hooks/useLocalPreference";
export enum WaveVotingState {
  NOT_STARTED = "NOT_STARTED",
  ONGOING = "ONGOING",
  ENDED = "ENDED",
}

// Define a type for the updateAvailableTabs parameters
type WaveTabParams = {
  waveId: string | null;
  isChatWave: boolean;
  hasAuthenticatedProfile: boolean;
  isMemesWave: boolean;
  isCurationWave: boolean;
  votingState: WaveVotingState;
  hasFirstDecisionPassed: boolean;
  transientPreferredTab?: MyStreamWaveTab | null;
};
export interface SetActiveContentTabOptions {
  readonly persist?: boolean;
}
export type SetActiveContentTab = (tab: MyStreamWaveTab, options?: SetActiveContentTabOptions) => void;
interface ContentTabContextType {
  activeContentTab: MyStreamWaveTab;
  setActiveContentTab: SetActiveContentTab;
  availableTabs: MyStreamWaveTab[];
  updateAvailableTabs: (params: WaveTabParams | null) => void;
}
const MEMES_WAVE_LAST_TAB_STORAGE_KEY = "memes_wave_last_tab_by_id";
const isValidWaveTab = (value: unknown): value is MyStreamWaveTab => Object.values(MyStreamWaveTab).includes(value as MyStreamWaveTab);
const isValidWaveTabMap = (value: unknown): value is Record<string, MyStreamWaveTab> => {
  if (value === null || value === undefined || typeof value !== "object") {
    return false;
  }
  return Object.values(value).every(tab => isValidWaveTab(tab));
};
const buildMemesTabs = (hasAuthenticatedProfile: boolean, votingState: WaveVotingState, hasFirstDecisionPassed: boolean) => {
  const tabs: MyStreamWaveTab[] = [];
  if (votingState !== WaveVotingState.ENDED) {
    tabs.push(MyStreamWaveTab.LEADERBOARD);
  }
  tabs.push(MyStreamWaveTab.CHAT);
  if (hasFirstDecisionPassed) {
    tabs.push(MyStreamWaveTab.WINNERS);
  }
  if (hasAuthenticatedProfile) {
    tabs.push(MyStreamWaveTab.MY_VOTES);
  }
  tabs.push(MyStreamWaveTab.OUTCOME);
  tabs.push(MyStreamWaveTab.FAQ);
  return tabs;
};
const buildDefaultTabs = (votingState: WaveVotingState, hasFirstDecisionPassed: boolean, isCurationWave: boolean) => {
  const tabs: MyStreamWaveTab[] = [MyStreamWaveTab.CHAT];
  if (votingState !== WaveVotingState.ENDED) {
    tabs.push(MyStreamWaveTab.LEADERBOARD);
  }
  if (isCurationWave) {
    tabs.push(MyStreamWaveTab.SALES);
  }
  if (hasFirstDecisionPassed) {
    tabs.push(MyStreamWaveTab.WINNERS);
  }
  if (!isCurationWave) {
    tabs.push(MyStreamWaveTab.OUTCOME);
  }
  if (isCurationWave) {
    tabs.push(MyStreamWaveTab.MY_VOTES);
  }
  return tabs;
};

// Create the context with a default value
const ContentTabContext = createContext<ContentTabContextType>({
  activeContentTab: MyStreamWaveTab.CHAT,
  setActiveContentTab: () => {},
  availableTabs: [MyStreamWaveTab.CHAT],
  updateAvailableTabs: () => {}
});

// Export a provider component
export const ContentTabProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(17);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tabsByWaveId, setTabsByWaveId] = useLocalPreference(MEMES_WAVE_LAST_TAB_STORAGE_KEY, t1, isValidWaveTabMap);
  const [activeContentTabRaw, setActiveContentTabRaw] = useState(MyStreamWaveTab.CHAT);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [MyStreamWaveTab.CHAT];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [availableTabs, setAvailableTabs] = useState(t2);
  const currentWaveIdRef = useRef(null);
  const tabsByWaveIdRef = useRef(tabsByWaveId);
  const transientTabOverrideRef = useRef(null);
  let t3;
  let t4;
  if ($[2] !== tabsByWaveId) {
    t3 = () => {
      tabsByWaveIdRef.current = tabsByWaveId;
    };
    t4 = [tabsByWaveId];
    $[2] = tabsByWaveId;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = tab => {
      setActiveContentTabRaw(tab);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const setActiveTabInternal = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = params => {
      if (!params) {
        setAvailableTabs([MyStreamWaveTab.CHAT]);
        currentWaveIdRef.current = null;
        transientTabOverrideRef.current = null;
        setActiveTabInternal(MyStreamWaveTab.CHAT);
        return;
      }
      const {
        waveId,
        isChatWave,
        hasAuthenticatedProfile,
        isMemesWave,
        isCurationWave,
        votingState,
        hasFirstDecisionPassed,
        transientPreferredTab
      } = params;
      let tabs;
      if (isChatWave) {
        tabs = [MyStreamWaveTab.CHAT];
      } else {
        if (isMemesWave) {
          tabs = buildMemesTabs(hasAuthenticatedProfile, votingState, hasFirstDecisionPassed);
        } else {
          tabs = buildDefaultTabs(votingState, hasFirstDecisionPassed, isCurationWave);
        }
      }
      if (transientTabOverrideRef.current !== null && transientTabOverrideRef.current.waveId !== waveId) {
        transientTabOverrideRef.current = null;
      }
      if (waveId !== null && transientPreferredTab !== null && transientPreferredTab !== undefined && tabs.includes(transientPreferredTab)) {
        transientTabOverrideRef.current = {
          waveId,
          tab: transientPreferredTab
        };
      }
      setAvailableTabs(tabs);
      currentWaveIdRef.current = waveId ?? null;
      const transientTab = waveId !== null && transientTabOverrideRef.current?.waveId === waveId ? transientTabOverrideRef.current.tab : null;
      if (transientTab !== null && tabs.includes(transientTab)) {
        setActiveTabInternal(transientTab);
        return;
      }
      if (transientTab !== null) {
        transientTabOverrideRef.current = null;
      }
      const storedTab = waveId ? tabsByWaveIdRef.current[waveId] : undefined;
      const defaultTab = isMemesWave && tabs.includes(MyStreamWaveTab.LEADERBOARD) ? MyStreamWaveTab.LEADERBOARD : MyStreamWaveTab.CHAT;
      const nextTab = storedTab !== undefined && tabs.includes(storedTab) ? storedTab : defaultTab;
      setActiveTabInternal(nextTab);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const updateAvailableTabs = t6;
  let t7;
  if ($[7] !== availableTabs || $[8] !== setTabsByWaveId) {
    t7 = (tab_0, options) => {
      if (availableTabs.includes(tab_0)) {
        setActiveTabInternal(tab_0);
        const waveId_0 = currentWaveIdRef.current;
        if (options?.persist === false) {
          transientTabOverrideRef.current = waveId_0 === null ? null : {
            waveId: waveId_0,
            tab: tab_0
          };
          return;
        }
        transientTabOverrideRef.current = null;
        if (waveId_0) {
          const nextMap = {
            ...tabsByWaveIdRef.current,
            [waveId_0]: tab_0
          };
          tabsByWaveIdRef.current = nextMap;
          setTabsByWaveId(nextMap);
        }
      } else {
        transientTabOverrideRef.current = null;
        setActiveTabInternal(MyStreamWaveTab.CHAT);
      }
    };
    $[7] = availableTabs;
    $[8] = setTabsByWaveId;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const setActiveContentTab = t7;
  let t8;
  if ($[10] !== activeContentTabRaw || $[11] !== availableTabs || $[12] !== setActiveContentTab) {
    t8 = {
      activeContentTab: activeContentTabRaw,
      setActiveContentTab,
      availableTabs,
      updateAvailableTabs
    };
    $[10] = activeContentTabRaw;
    $[11] = availableTabs;
    $[12] = setActiveContentTab;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const contextValue = t8;
  let t9;
  if ($[14] !== children || $[15] !== contextValue) {
    t9 = <ContentTabContext.Provider value={contextValue}>{children}</ContentTabContext.Provider>;
    $[14] = children;
    $[15] = contextValue;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};

// Export a hook for using the context
export const useContentTab = () => {
  return useContext(ContentTabContext);
};